import React, {Dispatch, ReactNode, SetStateAction, useState} from 'react'
import {Card, Row, P, Info, IconButton, Modal, Button, Colors} from '@components/UI/index.js'
import {formSpacing, Select} from '@components/UI/forms/index.js'
import {AddressesModel, AddressesModelReversed, ParcelsModel, ParcelsModelReversed, RoutesModel, StopsModel} from 'server/functions/analytics/analytics.types'
import {ViewData} from 'src/views/planner/Analytics'
import {Query} from 'src/stores/AnalyticsStore'

type ViewProps = {
    loading: boolean
    subGroup: string
    selectedGroup: string
    selectedSpecific: string
    groupingIsReversed: boolean
    legend: Legend
    setSelectedSpecific: Dispatch<SetStateAction<string>>
}

export type LeftViewProps = ViewProps & {
    leftViewData: ViewData[]
    subFilterBar?: AnalyticsViewProps['subFilterBar']
}

export type MainViewProps = ViewProps & {
    mainViewData: ViewData[]
    analyticsStartDate: string
    analyticsEndDate: string
    analyticsModel?: AddressesModel
    analyticsModelReversed?: AddressesModelReversed
    parcelsModel?: ParcelsModel
    parcelsModelReversed?: ParcelsModelReversed
    stopsModel?: StopsModel
    routesModel?: RoutesModel
    history?: any
}

export type Legend = {
    color: string
    label: string
    info?: string
    buttonInfo?: string
}[]

export type GroupOptions = {value: string, title: string, disabled?: boolean, info?: string, infoKeepWhiteSpace?: boolean}[]

type AnalyticsViewProps = {
    title: string
    icon: string
    key: string
    subFilterBar?: {
        groupOptions?: GroupOptions
        specificOptions?: {[key: string]: {value: string, title: string}[]}
    }
    LeftView?: (leftViewProps: ViewProps) => ReactNode
    leftViewProps?: LeftViewProps
    MainView?: (mainViewProps: ViewProps) => ReactNode
    mainViewProps?: MainViewProps
    // disabled?: boolean
    loading: boolean

    selectedGroup: string
    setSelectedGroup: (group: string) => void

    setSelectedSpecific: Dispatch<SetStateAction<string>>

    selectedSubGroup: string
    setSubGroup: (subGroup: string) => void

    showSubGroup: boolean

    legend: Legend

    filterOnOptions: {
        title: string
        value: string
    }[]
    filterOptions: {
        [filterOnKey: string]: {
            title: string
            value: string
        }[]
    }
    activeFilterQueries: Query[]
    onChangeQueries: (queries: Query[]) => void
    showFilterCard: boolean
    setShowFilterCard: Dispatch<SetStateAction<boolean>>
}

function AnalyticsView({subFilterBar, selectedGroup, setSelectedSpecific, setSubGroup, setSelectedGroup, selectedSubGroup, showSubGroup, legend, LeftView, leftViewProps, MainView, mainViewProps, filterOnOptions, filterOptions, activeFilterQueries, onChangeQueries, showFilterCard, setShowFilterCard}: AnalyticsViewProps) {
    const [showInfoPopup, setShowInfoPopup] = useState<boolean | string>(false)
    const [filterOn, setFilterOn] = useState<string>('')
    const [chosenFilterOption] = useState<string>('')
    const [operator, setOperator] = useState<Query['operator']>('=')

    return (
        <>
            {showFilterCard && (
                <Card style={{marginTop: 6, flex: 'unset', paddingLeft: 12, paddingRight: 12, minHeight: 60, display: 'flex', alignItems: 'center'}} shadow>
                    <Row style={{alignItems: 'center', gap: formSpacing.tight}}>
                        <Row style={{width: 'calc(16.5% + 12px + 10px)', gap: 10}}>
                            <Button
                                icon='mdi mdi-close'
                                onClick={() => {
                                    setShowFilterCard(false)
                                    onChangeQueries([])
                                }}
                                variant='outline'
                                style={{alignSelf: 'center'}}
                                tooltip='Filters verwijderen'
                            />
                            <Select
                                value={filterOn}
                                options={filterOnOptions.toSorted((a, b) => a.title.localeCompare(b.title))}
                                onChange={(e) => {
                                    setFilterOn(e.target.value)
                                }}
                                label='Filter op'
                            />
                        </Row>
                        <Select
                            style={{width: 'fit-content'}}
                            inputStyle={{minWidth: 50}}
                            value={operator}
                            onChange={(e) => {
                                setOperator(e.target.value as Query['operator'])
                            }}
                            options={[
                                {
                                    value: '=',
                                    title: '='
                                },
                                {
                                    value: '!=',
                                    title: '≠'
                                }
                            ]}
                        />
                        <Select
                            style={{marginLeft: 6, width: 'calc(16.5% - 30px)'}}
                            value={chosenFilterOption}
                            options={filterOptions[filterOn]?.toSorted((a, b) => a.title.localeCompare(b.title)) || []}
                            onChange={(e, isFreeMode) => {
                                let filterOperator = operator
                                if (operator === '=' && !isFreeMode) {
                                    filterOperator = '=='
                                }

                                onChangeQueries([...activeFilterQueries, {title: filterOnOptions.find((o) => o.value === filterOn)?.title, key: filterOn, operator: filterOperator, text: e.target.value}])
                            }}
                            allowFreeMode
                            freeModeAddValue
                            disabled={filterOn === ''}
                        />
                        {/* <Button
                                icon='mdi mdi-send'
                                onClick={() => {
                                    const newQueries: Query[] = chosenFilterOptions.map((option) => {
                                        return {
                                            title: filterOnOptions.find((o) => o.value === filterOn)?.title || '',
                                            key: filterOn,
                                            operator,
                                            text: option
                                        }
                                    })
                                    onChangeQueries([...activeFilterQueries, ...newQueries])
                                    setChosenFilterOptions([])
                                }}
                                variant='outline'
                                style={{alignSelf: 'center'}}
                            /> */}
                        <Row style={{width: 'fit-content', gap: 12, marginLeft: 48}}>
                            {activeFilterQueries.map((query, index) => (
                                <div
                                    key={`${query.key}-${query.operator}-${query.text}-${index}`}
                                    onClick={() => {
                                        const newQueries = activeFilterQueries.filter((_, i) => i !== index)
                                        onChangeQueries(newQueries)
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        // marginBottom: 0,
                                        // marginTop: 12,
                                        // marginLeft: 12,
                                        background: Colors.grey20,
                                        paddingLeft: 15,
                                        paddingRight: 15,
                                        height: 40,
                                        borderRadius: 20,
                                        color: Colors.textDark,
                                        boxShadow: '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0,0,0,.12)'
                                    }}
                                >
                                    {query.title || query.key} {query.operator === '!=' ? <i className='mdi mdi-not-equal-variant' /> : query.operator === '==' ? '=' : query.operator} {query.text}
                                    <i style={{marginLeft: 6, fontSize: 20, color: Colors.textDark, transform: 'translateX(3px)'}} className='mdi mdi-close-circle' />
                                </div>
                            ))}
                        </Row>
                    </Row>
                </Card>
            )}
            <Card style={{marginTop: 6, flex: 'unset', paddingLeft: 12, paddingRight: 12, minHeight: 60}} shadow>
                <Row style={{alignItems: 'center', gap: 10}}>
                    <Row style={{width: 'calc(33% + 12px)', gap: 10}}>
                        <Button
                            icon='mdi mdi-filter-outline'
                            onClick={() => setShowFilterCard(true)}
                            variant='outline'
                            style={{alignSelf: 'center'}}
                            tooltip='Filters toepassen'
                            disabled={showFilterCard}
                        />
                        {subFilterBar?.groupOptions && (
                            <Row style={{gap: 10}}>
                                <Select
                                    value={selectedGroup}
                                    onChange={(e) => {
                                        setSelectedSpecific('')
                                        setSubGroup('')
                                        setSelectedGroup(e.target.value)
                                    }}
                                    options={subFilterBar.groupOptions.toSorted((a, b) => a.title.localeCompare(b.title))}
                                    label='Groepeer op'
                                />
                                {showSubGroup ?
                                    (
                                        <Select
                                            value={selectedSubGroup}
                                            onChange={(e) => setSubGroup(e.target.value)}
                                            options={structuredClone(subFilterBar.groupOptions).map((option) => {
                                                if (option.value === selectedGroup || (option.value === 'postalCodeZone' && selectedGroup === 'deliveryZone') || (option.value === 'deliveryZone' && selectedGroup === 'postalCodeZone')) {
                                                    option.disabled = true
                                                }
                                                return option
                                            }).toSorted((a, b) => a.title.localeCompare(b.title))}
                                            label='Subverdeling'
                                            allowEmpty
                                        />
                                    ) :
                                    (
                                        <div style={{flex: 1}} />
                                    )}
                            </Row>
                        )}
                    </Row>
                    <Row style={{width: '66%', columnGap: 48, rowGap: 6, flexWrap: 'wrap', marginLeft: 48, minHeight: 48}}>
                        {legend.map((item, index) => (
                            <Row key={index} style={{columnGap: formSpacing.columnGap, alignItems: 'center', width: 'fit-content'}}>
                                <div style={{width: 20, height: 20, borderRadius: '50%', backgroundColor: item.color}}></div>
                                <P>{item.label}</P>
                                {item.info && (
                                    <Info text={item.info} textStyle={{whiteSpace: 'pre-line'}} style={{marginLeft: 0}} />
                                )}
                                {item.buttonInfo && (
                                    <IconButton onClick={() => setShowInfoPopup(item.buttonInfo)}>
                                        <i className='mdi mdi-information' />
                                    </IconButton>
                                )}
                            </Row>
                        ))}
                        <Modal
                            show={!!showInfoPopup}
                            closeButton
                            closeOnBackdropClick
                            onClose={() => setShowInfoPopup(false)}
                            title='Info'
                            style={{width: 600}}
                        >
                            <P style={{whiteSpace: 'pre-wrap'}}>{showInfoPopup}</P>
                        </Modal>
                    </Row>
                </Row>
            </Card>

            <div
                style={{
                    display: 'flex',
                    gap: 12,
                    flex: 1,
                    overflowY: 'hidden',
                    paddingBottom: 6
                }}
            >
                <div
                    style={{
                        width: '33%'
                        // overflow: 'auto'
                    }}
                >
                    {LeftView && leftViewProps && (
                        <LeftView {...leftViewProps} />
                    )}
                </div>
                <div style={{width: '66%', overflow: 'auto'}}>
                    {MainView && mainViewProps && (
                        <MainView {...mainViewProps} />
                    )}
                </div>
            </div>
        </>
    )
}

export default AnalyticsView
